// Custom SVG brand icons — inspired by the Moreno Quality Transportation icon sheet.
// True SVG with transparent backgrounds so they sit on any surface without a box.

const NAVY = "#0A192F";
const GREEN = "#10B981";
const YELLOW = "#F59E0B";
const RED = "#DC2626";
const BLUE = "#1D4ED8";
const PURPLE = "#7C3AED";

// ---------- Brand pillars ----------
export function SafetyShield({ size = 24, className }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M32 2L6 11v18c0 15.4 11 29.4 26 32 15-2.6 26-16.6 26-32V11L32 2z" fill={NAVY} />
      <path d="M20 32.5l8.5 8.5L45 24" stroke={GREEN} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export function WingedClock({ size = 24, className }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* left wing */}
      <path d="M8 24c-4 3-6 8-4 12 5 0 10-2 14-6l-2-8-8 2z" fill={NAVY} />
      <path d="M6 30l6-1" stroke={GREEN} strokeWidth="2" strokeLinecap="round" />
      {/* right wing */}
      <path d="M56 24c4 3 6 8 4 12-5 0-10-2-14-6l2-8 8 2z" fill={NAVY} />
      <path d="M58 30l-6-1" stroke={GREEN} strokeWidth="2" strokeLinecap="round" />
      {/* clock body */}
      <circle cx="32" cy="34" r="20" fill={NAVY} />
      <circle cx="32" cy="34" r="15" fill="#fff" />
      {/* hour marks */}
      <circle cx="32" cy="21" r="1.2" fill={NAVY} />
      <circle cx="32" cy="47" r="1.2" fill={NAVY} />
      <circle cx="19" cy="34" r="1.2" fill={NAVY} />
      <circle cx="45" cy="34" r="1.2" fill={NAVY} />
      {/* hands */}
      <path d="M32 24v10l7 4" stroke={NAVY} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export function TrustHandshake({ size = 24, className }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* heartbeat baseline */}
      <path d="M2 50h10l4-8 5 14 5-12 4 6h32" stroke={GREEN} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      {/* left forearm/sleeve */}
      <path d="M4 22l14-8 8 4-10 16-12-6z" fill={NAVY} />
      {/* right forearm/sleeve */}
      <path d="M60 22l-14-8-8 4 10 16 12-6z" fill={NAVY} />
      {/* clasped hands */}
      <path d="M18 22c3-2 7-2 10 0l4 3 4-3c3-2 7-2 10 0-3 6-8 11-14 13-6-2-11-7-14-13z" fill={NAVY} />
      <path d="M28 24l4 3M36 24l-4 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

// ---------- Star of Life ----------
export function StarOfLife({ size = 24, className }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g fill={NAVY}>
        <rect x="26" y="4" width="12" height="56" rx="2" />
        <rect x="26" y="4" width="12" height="56" rx="2" transform="rotate(60 32 32)" />
        <rect x="26" y="4" width="12" height="56" rx="2" transform="rotate(-60 32 32)" />
      </g>
      <path d="M28 22c0-2 2-3 4-3s4 1 4 3-3 5-3 10 1 8 3 10-2 3-4 3-4-1-4-3 3-5 3-10-3-8-3-10z" fill="#fff" />
    </svg>
  );
}

// ---------- Status circles ----------
function StatusCircle({ color, size = 20, children }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="16" cy="16" r="15" fill={color} />
      {children}
    </svg>
  );
}

export function StatusPending({ size = 20 }) {
  return (
    <StatusCircle color={YELLOW} size={size}>
      <path d="M16 8v9l6 3" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    </StatusCircle>
  );
}

export function StatusApproved({ size = 20 }) {
  return (
    <StatusCircle color={GREEN} size={size}>
      <path d="M9 16.5l4.5 4.5L23 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </StatusCircle>
  );
}

export function StatusRejected({ size = 20 }) {
  return (
    <StatusCircle color={RED} size={size}>
      <path d="M11 11l10 10M21 11L11 21" stroke="#fff" strokeWidth="3" strokeLinecap="round" fill="none" />
    </StatusCircle>
  );
}

export function StatusUnderReview({ size = 20 }) {
  return (
    <StatusCircle color={BLUE} size={size}>
      <circle cx="14.5" cy="14.5" r="4" stroke="#fff" strokeWidth="2.4" fill="none" />
      <path d="M18 18l4 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
    </StatusCircle>
  );
}

export function StatusInterview({ size = 20 }) {
  return (
    <StatusCircle color="#6366F1" size={size}>
      <path d="M11 10h10v6h-4l-3 3v-3h-3z" fill="#fff" />
    </StatusCircle>
  );
}

export function StatusNeedsInfo({ size = 20 }) {
  return (
    <StatusCircle color={PURPLE} size={size}>
      <circle cx="16" cy="10.5" r="1.6" fill="#fff" />
      <rect x="14.5" y="13.5" width="3" height="10" rx="1.2" fill="#fff" />
    </StatusCircle>
  );
}

export function statusIconFor(status, size = 20) {
  switch (status) {
    case "Application Received":
      return <StatusPending size={size} />;
    case "Under Review":
      return <StatusUnderReview size={size} />;
    case "Interview":
      return <StatusInterview size={size} />;
    case "Additional Information Required":
      return <StatusNeedsInfo size={size} />;
    case "Approved":
      return <StatusApproved size={size} />;
    case "Not Selected":
      return <StatusRejected size={size} />;
    default:
      return <StatusPending size={size} />;
  }
}
